import type { tableColumn } from "@/hooks/useTableSort";
import { useAlertsStore } from "@/stores/alerts";
import downloadFile from "@/utils";
import { unparse } from "papaparse";
import ReportPrintVue from "./ReportPrint.vue";
import ReportPrintMulVue from "./ReportPrintMultiple.vue";
import { createApp } from "vue";
import vuetify from "@/plugins/vuetify";
import useTeachers from "@/stores/school/teachers";
import useTeams from "@/stores/school/teams";
import useParents from "@/stores/school/parents";
import FetchService from "@/services/fetch.service";
import type { ReportPayload } from "./constants";
import { sort } from "@/utils/sort";
import dayjs from "dayjs";
import type { ConferenceDay } from "@/api_types/teacher";

export const exportTable = (
  columns: tableColumn<string>[] | Readonly<tableColumn<string>[]>,
  data: any[],
  name: string
) => {
  // Use display property instead of export property
  const exportColumns = columns.filter((col) => col.display !== false);
  const headers = exportColumns.map((col: tableColumn<string>) => col.title);
  const csvData = [
    headers,
    ...data.map((item) => exportColumns.map((col: tableColumn<string>) => item[col.key] ?? null))
  ];
  const str = unparse(csvData, {
    delimiter: ",",
    quotes: true
  });
  downloadFile(str, `${name}.csv`, "text/csv");

  useAlertsStore().addAlert("success", "Report has been exported successfully");
};

export const mapSync = async <T, S>(arr: T[], mapper: (item: T, index: number) => Promise<S>) => {
  const res: S[] = [];
  for (let i = 0; i < arr.length; i++) {
    res.push(await mapper(arr[i], i));
  }
  return res;
};

export const getMeetingTypeLabel = (value: string, t: (key: string) => string) => {
  if (!value) return "";
  const key = `message.AgendaCard.${value}`;
  const translated = t(key);
  return translated === key ? value : translated;
};

export type GroupedTableData = {
  dateLabel: string;
  roomText: string;
  virtualText: string;
  rows: any[][];
}[];

export const groupTableDataByDate = (
  rawRows: Record<string, any>[],
  visibleColumns: tableColumn<string>[],
  type?: string
): GroupedTableData => {
  if (!Array.isArray(rawRows) || rawRows.length === 0) return [];

  const firstRow = rawRows[0];
  const shouldGroup =
    firstRow?.timeslot &&
    ["teacher", "team"].includes(type || "");

  if (!shouldGroup) {
    const rows = rawRows.map((r) => visibleColumns.map((col) => r[col.key] ?? null));
    return [{ dateLabel: "", roomText: "", virtualText: "", rows }];
  }

  const sorted = [...rawRows].sort(
    (a, b) => dayjs(a.timeslot).valueOf() - dayjs(b.timeslot).valueOf()
  );

  const getRoom = (row: Record<string, any>) =>
    row.room || row.teacher_room || row.team_room || "";
  const getVirtual = (row: Record<string, any>) =>
    row.virtual_room ||
    row.teacher_virtual_room ||
    row.zoom_link ||
    row.google_meet_url ||
    row.microsoft_teams_url ||
    "";
  const hasInPerson = (row: Record<string, any>) =>
    row.meeting_type === "in_person" ||
    row.meeting_types?.in_person ||
    row.teacher_meeting_types?.in_person;

  const groups = new Map<
    string,
    { dateLabel: string; roomText: string; virtualText: string; hasInPerson: boolean; rows: Record<string, any>[] }
  >();

  for (const row of sorted) {
    const d = dayjs(row.timeslot);
    const dateKey = d.format("YYYY-MM-DD");
    const dateLabel = d.format("dddd, MMMM D, YYYY");

    if (!groups.has(dateKey)) {
      groups.set(dateKey, { dateLabel, roomText: "", virtualText: "", hasInPerson: false, rows: [] });
    }
    const group = groups.get(dateKey)!;
    group.hasInPerson = group.hasInPerson || hasInPerson(row);
    if (!group.roomText) {
      const room = getRoom(row);
      if (room) group.roomText = room;
    }
    if (!group.virtualText) {
      const virtual = getVirtual(row);
      if (virtual) group.virtualText = virtual;
    }
    group.rows.push(row);
  }

  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, group]) => ({
      dateLabel: group.dateLabel,
      roomText: group.hasInPerson ? (group.roomText || "Not Set Yet") : "",
      virtualText: group.virtualText,
      rows: group.rows.map((r) => visibleColumns.map((col) => r[col.key] ?? null))
    }));
};

export const printTable = (
  columns: tableColumn<string>[] | Readonly<tableColumn<string>[]>,
  data: any[],
  name: string,
  entityName?: string,
  type?: string
) => {
  // Use display property instead of print property
  const printColumns = columns.filter((col) => col.display !== false);
  const headers = printColumns.map((col: tableColumn<string>) => col.title);
  const groupedTableData = groupTableDataByDate(data, printColumns, type);

  const newWin = window.open("about:blank", "_blank")!;
  newWin.document.title = name + ".pdf";
  const meta = newWin.document.createElement("meta");
  meta.setAttribute("name", "viewport");
  meta.setAttribute("content", "width=device-width, initial-scale=1.0");

  const icons = newWin.document.createElement("link");
  icons.setAttribute(
    "href",
    "https://cdn.jsdelivr.net/npm/@mdi/font@7.2.96/css/materialdesignicons.min.css"
  );
  icons.setAttribute("rel", "stylesheet");
  // <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  newWin.document.head.appendChild(meta);
  newWin.document.head.appendChild(icons);
  const app = createApp(ReportPrintVue, {
    headers,
    groupedTableData,
    name,
    win: newWin,
    entityName
  }).use(vuetify);
  const container = newWin.document.createElement("div");
  container.classList.add("app");
  newWin.document.body.appendChild(container);
  app.mount(container);
  setTimeout(() => newWin.print(), 500);
};

export const printSelectedTable = async (
  columns: tableColumn<string>[] | Readonly<tableColumn<string>[]>,
  name: string,
  type: "teacher" | "parent" | "team",
  eventId: string,
  selectedEntityIds: string[],
  fetchInc: (n: number, inc: boolean) => any,
  selectedConferenceDayId?: string
) => {
  // Use display property to filter columns
  const visibleColumns = columns.filter((col) => col.display !== false);
  const headers = visibleColumns.map((col: tableColumn<string>) => col.title);

  const teachersS = useTeachers();
  const teamsS = useTeams();
  const parentsS = useParents();
  const entities = (() => {
    if (type == "parent")
      return sort(
        parentsS.parents.data.filter((i) => !i.deleted),
        (i) => i?.lastname + i?.firstname
      ).map((i) => ({
        title: i?.firstname + " " + i?.lastname,
        id: i.uid,
        sort_data: i?.lastname?.toLowerCase() + i?.firstname?.toLowerCase()
      }));
    if (type == "teacher")
      return sort(
        teachersS.teachers.data.filter((i) => !i.deleted),
        (i) => i?.teacher_lname + i?.teacher_fname
      ).map((i) => ({
        title: i?.teacher_fname + " " + i?.teacher_lname,
        id: i.uid,
        sort_data: i?.teacher_lname?.toLowerCase() + i?.teacher_fname?.toLowerCase()
      }));
    if (type == "team")
      return sort(
        teamsS.teams.data.filter((i) => !i.deleted),
        (i) => i.team_name
      ).map((i) => ({ title: i.team_name, id: i.uid, sort_data: i?.team_name?.toLowerCase() }));
    return [];
  })();
  const entity_uids = selectedEntityIds;
  fetchInc(entity_uids.length, false);
  const res: ReportPayload[] = [];
  for (const uid of entity_uids) {
    const r = await FetchService.fetchData(
      `/reports/${type}/${eventId}?${type}=${uid}`,
      "get",
      undefined,
      undefined
    );
    fetchInc(entity_uids?.length, true);
    if (r) {
      r.uid = uid;
      res.push(r);
    }
  }

  if (!res.length)
    return useAlertsStore().addAlert("info", `There aren't any ${type}s with appointments`);
  if (!Array.isArray(res))
    return useAlertsStore().addAlert(
      "error",
      "Failed to retrieve data for print. Please try again later."
    );
  const tableData = sort(
    (Array.isArray(res) ? res : [])
      .map((i) => {
        const entity = entities.find((e) => e.id == i.uid);
        if (["teacher", "team"].includes(type) && i.conference_days)
          i.table_data = addEmptyTimeSlot(i.table_data, i.conference_days);
        
        // Filter by conference day if selected
        let filteredTableData = i.table_data;
        if (selectedConferenceDayId) {
          filteredTableData = i.table_data.filter(
            (row: any) => row.conference_days_id === selectedConferenceDayId
          );
        }
        
        // Filter columns by display property
        const visibleCols = i.table_cols.filter((col: tableColumn<string>) => col.display !== false);
        const groupedData = groupTableDataByDate(filteredTableData, visibleCols, type);
        return {
          entity,
          groupedData,
          columns: visibleCols
        };
      })
      .filter((i) => i.entity),
    (i) => i.entity?.sort_data || ""
  );
  const newWin = window.open("about:blank", "_blank")!;
  newWin.document.title = name + ".pdf";
  const meta = newWin.document.createElement("meta");
  meta.setAttribute("name", "viewport");
  meta.setAttribute("content", "width=device-width, initial-scale=1.0");

  const icons = newWin.document.createElement("link");
  icons.setAttribute(
    "href",
    "https://cdn.jsdelivr.net/npm/@mdi/font@7.2.96/css/materialdesignicons.min.css"
  );
  icons.setAttribute("rel", "stylesheet");
  // <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  newWin.document.head.appendChild(meta);
  newWin.document.head.appendChild(icons);
  const app = createApp(ReportPrintMulVue, {
    headers,
    tableData: tableData.filter((i) => i.groupedData.some((g) => g.rows.length > 0)),
    name,
    win: newWin,
    type
  }).use(vuetify);
  const container = newWin.document.createElement("div");
  container.classList.add("app");
  newWin.document.body.appendChild(container);
  app.mount(container);
  setTimeout(() => newWin.print(), 500);
};

export const printAllTable = async (
  columns: tableColumn<string>[] | Readonly<tableColumn<string>[]>,
  name: string,
  type: "teacher" | "parent" | "team",
  eventId: string,
  fetchInc: (n: number, inc: boolean) => any,
  selectedConferenceDayId?: string
) => {
  // Use display property to filter columns
  const visibleColumns = columns.filter((col) => col.display !== false);
  const headers = visibleColumns.map((col: tableColumn<string>) => col.title);

  const teachersS = useTeachers();
  const teamsS = useTeams();
  const parentsS = useParents();
  const entities = (() => {
    if (type == "parent")
      return sort(
        parentsS.parents.data.filter((i) => !i.deleted),
        (i) => i?.lastname + i?.firstname
      ).map((i) => ({
        title: i?.firstname + " " + i?.lastname,
        id: i.uid,
        sort_data: i?.lastname?.toLowerCase() + i?.firstname?.toLowerCase()
      }));
    if (type == "teacher")
      return sort(
        teachersS.teachers.data.filter((i) => !i.deleted),
        (i) => i?.teacher_lname + i?.teacher_fname
      ).map((i) => ({
        title: i?.teacher_fname + " " + i?.teacher_lname,
        id: i.uid,
        sort_data: i?.teacher_lname?.toLowerCase() + i?.teacher_fname?.toLowerCase()
      }));
    if (type == "team")
      return sort(
        teamsS.teams.data.filter((i) => !i.deleted),
        (i) => i.team_name
      ).map((i) => ({ title: i.team_name, id: i.uid, sort_data: i?.team_name?.toLowerCase() }));
    return [];
  })();
  const entity_uids = (await FetchService.fetchData(
    `/reports/${type}_all/${eventId}`,
    "get",
    undefined,
    undefined
  )) as string[];
  fetchInc(entity_uids.length, false);
  const res: ReportPayload[] = [];
  for (const uid of entity_uids) {
    const r = await FetchService.fetchData(
      `/reports/${type}/${eventId}?${type}=${uid}`,
      "get",
      undefined,
      undefined
    );
    fetchInc(entity_uids?.length, true);
    if (r) {
      r.uid = uid;
      res.push(r);
    }
  }

  if (!res.length)
    return useAlertsStore().addAlert("info", `There aren't any ${type}s with appointments`);
  if (!Array.isArray(res))
    return useAlertsStore().addAlert(
      "error",
      "Failed to retrieve data for print. Please try again later."
    );
  const tableData = sort(
    (Array.isArray(res) ? res : [])
      .map((i) => {
        const entity = entities.find((e) => e.id == i.uid);
        if (["teacher", "team"].includes(type) && i.conference_days)
          i.table_data = addEmptyTimeSlot(i.table_data, i.conference_days);
        
        // Filter by conference day if selected
        let filteredTableData = i.table_data;
        if (selectedConferenceDayId) {
          filteredTableData = i.table_data.filter(
            (row: any) => row.conference_days_id === selectedConferenceDayId
          );
        }
        
        // Filter columns by display property
        const visibleCols = i.table_cols.filter((col: tableColumn<string>) => col.display !== false);
        const groupedData = groupTableDataByDate(filteredTableData, visibleCols, type);
        return {
          entity,
          groupedData,
          columns: visibleCols
        };
      })
      .filter((i) => i.entity),
    (i) => i.entity?.sort_data || ""
  );
  const newWin = window.open("about:blank", "_blank")!;
  newWin.document.title = name + ".pdf";
  const meta = newWin.document.createElement("meta");
  meta.setAttribute("name", "viewport");
  meta.setAttribute("content", "width=device-width, initial-scale=1.0");

  const icons = newWin.document.createElement("link");
  icons.setAttribute(
    "href",
    "https://cdn.jsdelivr.net/npm/@mdi/font@7.2.96/css/materialdesignicons.min.css"
  );
  icons.setAttribute("rel", "stylesheet");
  // <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  newWin.document.head.appendChild(meta);
  newWin.document.head.appendChild(icons);
  const app = createApp(ReportPrintMulVue, {
    headers,
    tableData: tableData.filter((i) => i.groupedData.some((g) => g.rows.length > 0)),
    name,
    win: newWin,
    type
  }).use(vuetify);
  const container = newWin.document.createElement("div");
  container.classList.add("app");
  newWin.document.body.appendChild(container);
  app.mount(container);
  setTimeout(() => newWin.print(), 500);
};

export function createDateArray(
  startDateTime: dayjs.Dayjs,
  endDateTime: dayjs.Dayjs,
  interval: number
) {
  const dateArray: dayjs.Dayjs[] = [];
  let currentDateTime = startDateTime;

  while (currentDateTime.isBefore(endDateTime)) {
    dateArray.push(currentDateTime);
    currentDateTime = currentDateTime.add(interval, "minute");
  }
  return dateArray;
}

export const addEmptyTimeSlot = <T extends Record<string, string | number>>(
  tableData: T[],
  conference_days: ConferenceDay[]
) => {
  console.log("addEmptyTimeSlot called with:", { 
    tableDataLength: tableData?.length || 0, 
    conferenceDaysLength: conference_days?.length || 0 
  });
  
  if (!Array.isArray(tableData) || !Array.isArray(conference_days)) {
    console.error("Invalid input to addEmptyTimeSlot:", { tableData, conference_days });
    return tableData || [];
  }
  
  const conferenceDaysSplit: Record<string, T[]> = Object.fromEntries(
    conference_days.map((i) => [i.conference_days_id, []])
  );

  for (const row of tableData) {
    // Handle case where conference_days_id is missing
    const dayId = row.conference_days_id;
    if (dayId && conferenceDaysSplit[dayId]) {
      conferenceDaysSplit[dayId].push(row);
    } else {
      // If no conference_days_id, use the first conference day as fallback
      if (conference_days.length > 0) {
        const firstDayId = conference_days[0].conference_days_id;
        conferenceDaysSplit[firstDayId]?.push(row);
      }
    }
  }

  for (const conference_day of conference_days) {
    const passing_time = conference_day?.passing_time || 0;
    const time_interval = conference_day?.time_interval || 5;
    const start = conference_day!.start;
    const end = conference_day!.end;

    const period = createDateArray(dayjs(start), dayjs(end), time_interval + passing_time)
      .map((i) => i.format("YYYY-MM-DD HH:mm:ss"))
      .filter(
        (i) => !conferenceDaysSplit[conference_day.conference_days_id].find((r) => r.timeslot == i)
      );

    const data = period.map(
      (i) =>
        ({
          timeslot: i,
          time_formatted: dayjs(i).format("MMM DD h:mm A"),
          status: "Available"
        } as any as T)
    );
    conferenceDaysSplit[conference_day.conference_days_id].push(...data);
  }
  
  const result = sort(Object.values(conferenceDaysSplit).flat(), (i) => i.timeslot);
  console.log("addEmptyTimeSlot result:", { 
    resultLength: result?.length || 0
  });
  return result;
};
